import { createContext, forwardRef, type HTMLAttributes, useContext, useEffect, useMemo, useState } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { QuizsetCursorSearchReqContentType } from '@/api/gen/_model/quizsetCursorSearchReqContentType';
import { useViewport } from '@/hooks';
import { ArrowTurnDownrightIcon, CaretDownIcon, CheckSmallIcon, MinusIcon, PlusIcon, ResetIcon, XSmallCircleColorIcon, XSmallIcon } from '@/icons';
import { usePublicCategories, usePublicCategoryFilter } from '@/module/public/hooks';
import { CONTENT_TYPE_OPTIONS, CONTENT_TYPE_SECTION_LABEL, isContentTypeSelected, useLibraryContentTypeQuery } from '@/module/public/hooks/useLibraryContentTypeQuery';
import { usePublicSearchFilterQuery } from '@/module/public/hooks/usePublicSearchFilterQuery';
import { type MapTemplateType, MapTemplateTypeLocaleKey, MapTemplateTypes } from '@/types';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui';
import { IconButton } from '@/ui_v3/components/button';
import { Checkbox } from '@/ui_v3/components/control/checkbox';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/ui_v3/components/drawer';
import { cn } from '@/utils/tailwind.util';
import { isAllGrades } from '@/views/build/util';
const QUESTION_SIZE_OPTIONS = ['1-5', '6-10', '11-15', '16-20'];
const capitalizeFilterLabel = (label: string) => {
  const [firstLetter = '', ...restLetters] = Array.from(label);
  return `${firstLetter.toLocaleUpperCase()}${restLetters.join('')}`;
};
const formatGradeSemesterPlaceholder = (grade: string, semester: string) => `${capitalizeFilterLabel(grade)} · ${capitalizeFilterLabel(semester)}`;
const SearchFilterOverlayContext = createContext<{
  close: () => void;
  variant: 'dropdown' | 'drawer';
}>({
  close: () => undefined,
  variant: 'dropdown'
});
const useSearchFilterOverlay = () => useContext(SearchFilterOverlayContext);

// 모바일 Drawer(바텀시트)는 화면 전체를 덮는 오버레이라 GNB 위로 떠야 한다.
const SEARCH_FILTER_OVERLAY_Z_INDEX = 'z-[100000000]';
// 데스크탑 드롭다운은 GNB(HeaderContainer, z-40) 아래로 깔려야 한다(본문 위, GNB 아래).
const SEARCH_FILTER_DROPDOWN_Z_INDEX = 'z-30';
export const SearchFilterBar = ({
  className
}: {
  className?: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    selectedSchoolTypeName
  } = usePublicCategoryFilter();
  const searchFilter = usePublicSearchFilterQuery();
  const {
    contentType,
    toggleContentType,
    resetContentType
  } = useLibraryContentTypeQuery();
  const [expandedSubjectGroupIds, setExpandedSubjectGroupIds] = useState<string[]>([]);
  const gradeFilterLabel = String(t('common:school.info.grade'));
  const semesterFilterLabel = String(t('common:school.info.semester'));
  const categoryState = useMemo(() => ({
    schoolType: selectedSchoolTypeName,
    grades: searchFilter.state.grades,
    semesters: searchFilter.state.semesters,
    subject: searchFilter.state.subject
  }), [searchFilter.state.grades, searchFilter.state.semesters, searchFilter.state.subject, selectedSchoolTypeName]);
  const {
    subjectGroups,
    grades,
    semesters,
    publishers
  } = usePublicCategories(categoryState);
  const hasSubjectOptions = Object.keys(subjectGroups).length > 0;
  const hasSpecificSchoolType = Boolean(selectedSchoolTypeName) && selectedSchoolTypeName?.toUpperCase() !== 'ALL';
  const canUseGradeSemesterFilter = hasSpecificSchoolType && ['ELEMENTARY', 'PRIMARY', 'MIDDLE', 'HIGH'].includes(selectedSchoolTypeName.toUpperCase());
  const hasGradeSemesterOptions = grades.length > 0;
  const canUsePublisherFilter = hasSpecificSchoolType && selectedSchoolTypeName.toUpperCase() !== 'KINDERGARTEN';
  const selectedGradesInMenu = useMemo(() => {
    const gradeValues = new Set(grades.map(grade => grade.value));
    return searchFilter.state.grades.filter(grade => gradeValues.has(grade));
  }, [grades, searchFilter.state.grades]);
  const selectedSemestersInMenu = useMemo(() => {
    const semesterValues = new Set(semesters.map(semester => semester.value));
    return searchFilter.state.semesters.filter(semester => semesterValues.has(semester));
  }, [searchFilter.state.semesters, semesters]);
  const hasSelectedGradeSemester = selectedGradesInMenu.length > 0 || selectedSemestersInMenu.length > 0;
  const hasPublisherOptions = publishers.length > 0;
  const shouldShowGradeSemesterFilter = canUseGradeSemesterFilter && hasGradeSemesterOptions;
  const shouldSelectGradeBeforePublisher = selectedGradesInMenu.length === 0;
  const shouldShowPublisherFilter = canUsePublisherFilter && hasPublisherOptions;
  const selectedSubjectLabel = useMemo(() => {
    const selectedSubject = searchFilter.state.subject;
    if (!selectedSubject) return '';
    if (selectedSubject.length === 4) {
      const mainSubjectGroupId = selectedSubject.slice(0, 2);
      return String(subjectGroups[mainSubjectGroupId]?.children[selectedSubject]?.name ?? '');
    }
    return String(subjectGroups[selectedSubject]?.name ?? '');
  }, [searchFilter.state.subject, subjectGroups]);
  useEffect(() => {
    const selectedSubject = searchFilter.state.subject;
    const selectedMainSubjectGroupId = selectedSubject.length === 4 ? selectedSubject.slice(0, 2) : '';
    if (!selectedMainSubjectGroupId) return;
    setExpandedSubjectGroupIds(current => current.includes(selectedMainSubjectGroupId) ? current : [...current, selectedMainSubjectGroupId]);
  }, [searchFilter.state.subject]);
  const toggleSubjectGroup = (subjectGroupId: string) => {
    setExpandedSubjectGroupIds(current => current.includes(subjectGroupId) ? current.filter(value => value !== subjectGroupId) : [...current, subjectGroupId]);
  };
  const gradeSemesterLabel = useMemo(() => {
    const labels = [...selectedGradesInMenu.map(grade => isAllGrades(grade) ? String(t('common:school.info.allGrades')) : String(t('common:school.info.nGrade', {
      n: Number(grade)
    }))), ...selectedSemestersInMenu.map(semester => String(t('common:school.info.nSemester', {
      n: semester
    })))];
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    return `${labels[0]} 외 ${labels.length - 1}`;
  }, [selectedGradesInMenu, selectedSemestersInMenu, t]);
  const publisherLabel = useMemo(() => {
    const selectedPublishers = searchFilter.state.publishers;
    if (selectedPublishers.length === 0) return '';
    const firstLabel = publishers.find(publisher => publisher.value === selectedPublishers[0])?.label ?? selectedPublishers[0];
    if (selectedPublishers.length === 1) return firstLabel;
    return `${firstLabel} 외 ${selectedPublishers.length - 1}`;
  }, [publishers, searchFilter.state.publishers]);
  const extraCount = [searchFilter.state.official, searchFilter.state.templateType, searchFilter.state.quizCountRange,
  // 전체(ALL)는 기본값이라 세지 않는다.
  contentType !== QuizsetCursorSearchReqContentType.ALL].filter(Boolean).length;
  const extraFilterLabel = useMemo(() => {
    const labels = [searchFilter.state.official ? String(t('common:common.onlyOfficialQuiz')) : '', searchFilter.state.templateType ? String(t(MapTemplateTypeLocaleKey[searchFilter.state.templateType as MapTemplateType])) : '', searchFilter.state.quizCountRange ? String(t('common:play.header.question', {
      totalCount: searchFilter.state.quizCountRange
    })) : ''].filter(Boolean);
    if (labels.length === 0) {
      return String(t('common:school.levelShort.other'));
    }
    if (labels.length === 1) return labels[0];
    return `${labels[0]} 외 ${labels.length - 1}`;
  }, [searchFilter.state.official, searchFilter.state.quizCountRange, searchFilter.state.templateType, t]);
  return <div className={cn('flex w-full flex-wrap items-center gap-3xs', className)} data-sentry-component="SearchFilterBar" data-sentry-source-file="SearchFilterBar.tsx">
      {hasSubjectOptions && <SearchFilterDropdown label={selectedSubjectLabel || String(t('common:category.subject'))} title={String(t('common:category.subject'))} selected={Boolean(searchFilter.state.subject)} onClear={() => searchFilter.setSubject('')}>
          <div>
            {Object.entries(subjectGroups).sort((a, b) => a[1].order - b[1].order).map(([mainSubjectGroupId, mainSubject]) => {
          const subSubjects = Object.entries(mainSubject.children).filter(([childGroupId]) => childGroupId !== mainSubjectGroupId).sort((a, b) => a[1].order - b[1].order);
          const hasSubSubjects = subSubjects.length > 0;
          const isExpanded = expandedSubjectGroupIds.includes(mainSubjectGroupId);
          return <SubjectFilterGroup key={mainSubjectGroupId} mainSubjectName={mainSubject.name} mainSubjectGroupId={mainSubjectGroupId} subSubjects={subSubjects} selectedSubjectId={searchFilter.state.subject} expanded={isExpanded} onToggleExpand={() => toggleSubjectGroup(mainSubjectGroupId)} onSelectSubject={subjectId => {
            void searchFilter.setSubject(subjectId);
            if (subjectId === mainSubjectGroupId && hasSubSubjects) {
              setExpandedSubjectGroupIds(current => current.includes(mainSubjectGroupId) ? current : [...current, mainSubjectGroupId]);
            }
          }} hasSubSubjects={hasSubSubjects} />;
        })}
          </div>
        </SearchFilterDropdown>}

      {shouldShowGradeSemesterFilter && <SearchFilterDropdown label={gradeSemesterLabel || formatGradeSemesterPlaceholder(gradeFilterLabel, semesterFilterLabel)} selected={hasSelectedGradeSemester} onClear={() => {
      void searchFilter.setGrades([]);
      void searchFilter.setSemesters([]);
    }}>
          <div>
            {grades.length > 0 && <SearchFilterMenuHeader label={capitalizeFilterLabel(gradeFilterLabel)} />}
            {grades.map(grade => <SearchFilterMenuItem key={grade.value} checkbox label={isAllGrades(grade.value) ? String(t('common:school.info.allGrades')) : String(t('common:school.info.nGrade', {
          n: Number(grade.value)
        }))} selected={searchFilter.state.grades.includes(grade.value)} onSelect={() => searchFilter.toggleGrade(grade.value)} />)}
            {semesters.length > 0 && <SearchFilterMenuHeader label={capitalizeFilterLabel(semesterFilterLabel)} />}
            {semesters.map(semester => <SearchFilterMenuItem key={semester.value} checkbox label={String(t('common:school.info.nSemester', {
          n: semester.value
        }))} selected={searchFilter.state.semesters.includes(semester.value)} onSelect={() => searchFilter.toggleSemester(semester.value)} />)}
          </div>
        </SearchFilterDropdown>}

      {shouldShowPublisherFilter && <SearchFilterDropdown label={publisherLabel || String(t('common:schoolInfo.textbookPublisher'))} selected={searchFilter.state.publishers.length > 0} onClear={() => {
      void searchFilter.setPublishers([]);
    }}>
          <div>
            {shouldSelectGradeBeforePublisher ? <SearchFilterMenuGuide label={String(t('common:required.firstGrade'))} /> : publishers.map(publisher => <SearchFilterMenuItem key={publisher.value} checkbox label={publisher.label} selected={searchFilter.state.publishers.includes(publisher.value)} onSelect={() => searchFilter.togglePublisher(publisher.value)} />)}
          </div>
        </SearchFilterDropdown>}

      <SearchFilterDropdown label={extraFilterLabel} selected={extraCount > 0} onClear={() => {
      void searchFilter.setOfficial(false);
      void searchFilter.setTemplateType('');
      void searchFilter.setQuizCountRange('');
      resetContentType();
    }} data-sentry-element="SearchFilterDropdown" data-sentry-source-file="SearchFilterBar.tsx">
        <div>
          <SearchFilterMenuItem label={String(t('common:common.onlyOfficialQuiz'))} selected={searchFilter.state.official} onSelect={() => searchFilter.setOfficial(!searchFilter.state.official)} data-sentry-element="SearchFilterMenuItem" data-sentry-source-file="SearchFilterBar.tsx" />
          <SearchFilterMenuDivider data-sentry-element="SearchFilterMenuDivider" data-sentry-source-file="SearchFilterBar.tsx" />
          {/*
            자료 유형(퀴즈·폴더). 둘 다 켜면 전체다 — 서버 `contentType` 이 ALL/QUIZSET/FOLDER
            셋 중 하나라, 체크 두 개를 그 값으로 접어 보낸다(둘 다 끄면 전체로 되돌린다).
           */}
          <SearchFilterMenuHeader label={CONTENT_TYPE_SECTION_LABEL} data-sentry-element="SearchFilterMenuHeader" data-sentry-source-file="SearchFilterBar.tsx" />
          {CONTENT_TYPE_OPTIONS.map(option => <SearchFilterMenuItem key={option.value} checkbox label={option.label} selected={isContentTypeSelected(contentType, option.value)} onSelect={() => toggleContentType(option.value)} />)}
          <SearchFilterMenuDivider data-sentry-element="SearchFilterMenuDivider" data-sentry-source-file="SearchFilterBar.tsx" />
          <SearchFilterMenuHeader label={String(t('common:common.quizMapType'))} data-sentry-element="SearchFilterMenuHeader" data-sentry-source-file="SearchFilterBar.tsx" />
          {MapTemplateTypes.filter(type => type !== 'total').map(type => <SearchFilterMenuItem key={type} label={String(t(MapTemplateTypeLocaleKey[type]))} selected={searchFilter.state.templateType === type} onSelect={() => searchFilter.setTemplateType(searchFilter.state.templateType === type ? '' : type)} />)}
          <SearchFilterMenuDivider data-sentry-element="SearchFilterMenuDivider" data-sentry-source-file="SearchFilterBar.tsx" />
          <SearchFilterMenuHeader label={String(t('common:public.questionSize'))} data-sentry-element="SearchFilterMenuHeader" data-sentry-source-file="SearchFilterBar.tsx" />
          {QUESTION_SIZE_OPTIONS.map(option => <SearchFilterMenuItem key={option} label={String(t('common:play.header.question', {
          totalCount: option
        }))} selected={searchFilter.state.quizCountRange === option} onSelect={() => searchFilter.setQuizCountRange(searchFilter.state.quizCountRange === option ? '' : option)} />)}
        </div>
      </SearchFilterDropdown>

      {searchFilter.hasActiveSearchFilters && <IconButton variant={'transparent'} intent={'neutral'} size={'xs'} onClick={searchFilter.clearSearchFilters} className={'shrink-0'}>
          <ResetIcon size={'xs'} fill={ICON_COLOR.neutral} />
        </IconButton>}
    </div>;
};
const SearchFilterDropdown = ({
  label,
  selected,
  onClear,
  title,
  children
}: {
  label: string;
  selected: boolean;
  onClear: () => void;
  title?: string;
  children: React.ReactNode;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    isMobile
  } = useViewport();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const trigger = <SearchFilterTrigger label={label} selected={selected} onClear={onClear} onOpen={() => setOpen(true)} />;
  if (isMobile) {
    return <Drawer open={open} onOpenChange={nextOpen => {
      if (!nextOpen) close();
    }}>
        <DrawerTrigger asChild onClick={() => setOpen(true)}>
          {trigger}
        </DrawerTrigger>
        <DrawerContent className={cn(SEARCH_FILTER_OVERLAY_Z_INDEX, 'max-h-[50dvh] w-full rounded-t-md border-0 bg-default px-md focus:!shadow-none', 'shadow-[0_20px_32px_0_rgba(28,27,31,0.12),0_0_20px_0_rgba(28,27,31,0.08)]')} hasDim={true} hasDrawerHandle={false} onDimClick={close}>
          <DrawerHeader className="flex items-center justify-between pb-xs pt-md text-heading-md-bold">
            <DrawerTitle>{title ?? label}</DrawerTitle>
            <IconButton variant={'transparent'} intent="neutral" size="sm" onClick={close} aria-label={`${label} ${String(t('common:public.filter'))} ${String(t('common:global.close.button'))}`}>
              <XSmallCircleColorIcon size={'md'} />
            </IconButton>
          </DrawerHeader>
          <SearchFilterOverlayContext.Provider value={{
          close,
          variant: 'drawer'
        }}>
            <div className={'h-full overflow-y-auto pb-lg pt-xs [&>div]:w-full [&>div]:min-w-0'} style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'auto'
          }}>
              {children}
            </div>
          </SearchFilterOverlayContext.Provider>
        </DrawerContent>
      </Drawer>;
  }
  return <DropdownMenu modal={false} open={open} onOpenChange={setOpen} data-sentry-element="DropdownMenu" data-sentry-component="SearchFilterDropdown" data-sentry-source-file="SearchFilterBar.tsx">
      <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="SearchFilterBar.tsx">{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={'start'} className={cn(SEARCH_FILTER_DROPDOWN_Z_INDEX, 'max-h-[530px] w-[280px] overflow-y-auto rounded-sm border border-assistive bg-default px-xs py-3xs', 'shadow-[0_4px_12px_0_rgba(28,27,31,0.12),0_0_16px_0_rgba(28,27,31,0.04)]')} data-sentry-element="DropdownMenuContent" data-sentry-source-file="SearchFilterBar.tsx">
        <SearchFilterOverlayContext.Provider value={{
        close,
        variant: 'dropdown'
      }} data-sentry-element="unknown" data-sentry-source-file="SearchFilterBar.tsx">
          {children}
        </SearchFilterOverlayContext.Provider>
      </DropdownMenuContent>
    </DropdownMenu>;
};
type SearchFilterTriggerProps = HTMLAttributes<HTMLDivElement> & {
  label: string;
  selected: boolean;
  onClear: () => void;
  onOpen: () => void;
};
const SearchFilterTrigger = forwardRef<HTMLDivElement, SearchFilterTriggerProps>(({
  label,
  selected,
  onClear,
  onOpen,
  className,
  ...props
}, ref) => <div ref={ref} {...props} className={cn('group flex h-[36px] min-w-[36px] items-center overflow-hidden rounded-sm text-neutral transition-colors', selected ? 'border border-[rgba(28,27,31,0.04)] bg-neutral hover:bg-[#EEF0F5]' : 'bg-transparent hover:bg-gray-50', className)}>
    <button type={'button'} data-no-focus-visible className={cn('flex h-full items-center gap-3xs pl-sm text-body-sm-semibold transition-colors focus:outline-none', selected ? 'pr-2xs' : 'pr-sm')} onClick={() => {
    onOpen();
  }}>
      {label}
      {!selected && <CaretDownIcon size={'xs'} fill={ICON_COLOR.neutral} />}
    </button>
    {selected && <button type={'button'} data-no-focus-visible className={'flex h-full min-w-[28px] items-center justify-center pr-sm transition-colors'} aria-label={`${label} 필터 해제`} onPointerDown={event => {
    event.preventDefault();
    event.stopPropagation();
  }} onClick={event => {
    event.preventDefault();
    event.stopPropagation();
    onClear();
  }}>
        <XSmallIcon size={'sm'} fill={ICON_COLOR.neutral} />
      </button>}
  </div>);
SearchFilterTrigger.displayName = 'SearchFilterTrigger';
const SearchFilterMenuItemFrame = ({
  className,
  onClick,
  children
}: {
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) => {
  const {
    close,
    variant
  } = useSearchFilterOverlay();
  const handleSelect = () => {
    onClick?.();
    close();
  };
  if (variant === 'dropdown') {
    return <DropdownMenuItem className={className} onClick={handleSelect}>
        {children}
      </DropdownMenuItem>;
  }
  return <div role="menuitem" tabIndex={0} className={cn('cursor-pointer select-none h-[44px] min-h-[44px] text-body-lg-medium', className)} onClick={handleSelect} onKeyDown={event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleSelect();
  }} data-sentry-component="SearchFilterMenuItemFrame" data-sentry-source-file="SearchFilterBar.tsx">
      {children}
    </div>;
};
const SearchFilterMenuHeader = ({
  label
}: {
  label: string;
}) => <div className={'px-2xs py-xs text-caption-lg-semibold text-assistive'} data-sentry-component="SearchFilterMenuHeader" data-sentry-source-file="SearchFilterBar.tsx">
    {label}
  </div>;
const SearchFilterMenuDivider = () => <div className={'my-3xs h-px w-full bg-alternative'} data-sentry-component="SearchFilterMenuDivider" data-sentry-source-file="SearchFilterBar.tsx" />;
const SearchFilterMenuGuide = ({
  label
}: {
  label: string;
}) => <div role="menuitem" className={'flex h-[36px] min-h-[36px] items-center rounded-2xs px-2xs py-4xs text-body-md-medium text-assistive'} data-sentry-component="SearchFilterMenuGuide" data-sentry-source-file="SearchFilterBar.tsx">
    <span className={'min-w-0 flex-1 truncate'}>{label}</span>
  </div>;
const SubjectFilterGroup = ({
  mainSubjectName,
  mainSubjectGroupId,
  subSubjects,
  selectedSubjectId,
  expanded,
  hasSubSubjects,
  onToggleExpand,
  onSelectSubject
}: {
  mainSubjectName: string;
  mainSubjectGroupId: string;
  subSubjects: Array<[string, {
    name: string;
    order: number;
  }]>;
  selectedSubjectId: string;
  expanded: boolean;
  hasSubSubjects: boolean;
  onToggleExpand: () => void;
  onSelectSubject: (subjectId: string) => void;
}) => {
  const selected = selectedSubjectId === mainSubjectGroupId;
  return <div data-sentry-component="SubjectFilterGroup" data-sentry-source-file="SearchFilterBar.tsx">
      <SearchFilterMenuItemFrame className={cn('flex h-[36px] min-h-[36px] w-full items-center gap-3xs rounded-2xs px-2xs py-4xs text-body-md-medium', selected ? 'bg-strong-alternative text-strong' : 'text-default')} onClick={() => onSelectSubject(mainSubjectGroupId)} data-sentry-element="SearchFilterMenuItemFrame" data-sentry-source-file="SearchFilterBar.tsx">
        <span className={'min-w-0 flex-1 truncate'}>{mainSubjectName}</span>
        {selected && <CheckSmallIcon size={'xs'} fill={ICON_COLOR.strong} />}
        {hasSubSubjects && <button type={'button'} className={'flex size-[30px] shrink-0 items-center justify-center rounded-xs'} aria-label={`${mainSubjectName} 하위 과목 ${expanded ? '닫기' : '열기'}`} onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onToggleExpand();
      }}>
            {expanded ? <MinusIcon size={'xs'} fill={ICON_COLOR.assistive} /> : <PlusIcon size={'xs'} fill={ICON_COLOR.assistive} />}
          </button>}
      </SearchFilterMenuItemFrame>
      {expanded && subSubjects.map(([subSubjectGroupId, subSubject]) => {
      const subSubjectSelected = selectedSubjectId === subSubjectGroupId;
      return <SearchFilterMenuItemFrame key={subSubjectGroupId} className={cn('flex h-[36px] min-h-[36px] w-full items-center gap-3xs rounded-none py-4xs pl-md pr-2xs text-body-md-medium', subSubjectSelected ? 'bg-strong-alternative text-strong' : 'text-default')} onClick={() => onSelectSubject(subSubjectGroupId)}>
              <ArrowTurnDownrightIcon width={14} height={14} className={'shrink-0 text-assistive'} />
              <span className={'min-w-0 flex-1 truncate'}>
                {subSubject.name}
              </span>
              {subSubjectSelected && <CheckSmallIcon size={'xs'} fill={ICON_COLOR.strong} />}
            </SearchFilterMenuItemFrame>;
    })}
    </div>;
};
const SearchFilterMenuItem = ({
  label,
  selected,
  onSelect,
  checkbox = false
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  checkbox?: boolean;
}) => <SearchFilterMenuItemFrame className={cn('flex h-[36px] min-h-[36px] items-center gap-3xs rounded-2xs px-2xs py-4xs text-body-md-medium', selected ? 'bg-strong-alternative text-strong' : 'text-default')} onClick={() => {
  onSelect();
}} data-sentry-element="SearchFilterMenuItemFrame" data-sentry-component="SearchFilterMenuItem" data-sentry-source-file="SearchFilterBar.tsx">
    {checkbox && <Checkbox checked={selected} size={'xs'} tabIndex={-1} aria-hidden className={'pointer-events-none -m-4xs shrink-0'} />}
    <span className={'min-w-0 flex-1 truncate'}>{label}</span>
    {!checkbox && selected && <CheckSmallIcon size={'xs'} fill={ICON_COLOR.strong} />}
  </SearchFilterMenuItemFrame>;